import React, { useEffect, useState } from 'react';
import { api } from './auth';

export default function AdminUsers({ user }) {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'verifier', company_id: '', full_name: '' });
  const [inviteResult, setInviteResult] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [tab, setTab] = useState('users');

  const load = async () => {
    const u = await api.get('/admin/users'); setUsers(u.data);
    const inv = await api.get('/admin/invitations'); setInvitations(inv.data);
    if (user.role === 'super_admin') {
      const c = await api.get('/admin/companies'); setCompanies(c.data);
    }
  };
  useEffect(() => { load(); }, []);

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviteResult(null);
    const payload = { ...inviteForm };
    if (user.role !== 'super_admin') delete payload.company_id;
    else if (payload.company_id) payload.company_id = parseInt(payload.company_id);
    if (!payload.full_name) delete payload.full_name;
    try {
      const r = await api.post('/admin/invite', payload);
      const link = `${window.location.origin}${r.data.invite_link}`;
      setInviteResult({ link, expires: r.data.expires_at });
      setInviteForm({ email: '', role: 'verifier', company_id: '', full_name: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Invitation failed');
    }
  };

  const deactivate = async (uid) => {
    if (!window.confirm('Deactivate this user?')) return;
    await api.patch(`/admin/users/${uid}/deactivate`);
    load();
  };

  const resetPassword = async (uid, email, name) => {
    if (!window.confirm(
      `Generate a password reset link for ${name || email}?\n\n` +
      `Any previous unused reset link for this user will be invalidated. ` +
      `You'll get a one-time link valid for 24 hours to send to the user via Slack/Signal/email.`
    )) return;
    try {
      const r = await api.post(`/admin/users/${uid}/reset-password`);
      const link = `${window.location.origin}${r.data.reset_path}`;
      setResetResult({
        link,
        userEmail: r.data.user_email,
        userName: r.data.user_name,
        expiresAt: r.data.expires_at,
      });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate reset link');
    }
  };

  const reactivate = async (uid, name) => {
    if (!window.confirm(`Reactivate ${name || 'this user'}? They will be able to sign in again.`)) return;
    try {
      await api.patch(`/admin/users/${uid}/reactivate`);
      load();
    } catch (e) {
      // Surface as much useful diagnostic info as possible — generic
      // "Reactivation failed" is unhelpful when a real error has detail.
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      if (detail) {
        alert(detail);
      } else if (status === 404) {
        alert(
          'Reactivation endpoint not found (HTTP 404).\n\n' +
          'This usually means the backend is still running an older version. ' +
          'Confirm v5.0.2 or later is deployed by visiting ' +
          window.location.origin.replace(/-frontend/, '') + '/health and checking the version.'
        );
      } else if (status === 401) {
        alert('Your session has expired. Please sign in again.');
      } else if (status === 403) {
        alert('You do not have permission to reactivate this user.');
      } else if (status === 0 || !status) {
        alert(
          'Network error — could not reach the backend.\n\n' +
          'Possible causes: the backend is down, CORS is blocking the request, ' +
          'or your internet connection dropped. Check the browser console (F12) for details.'
        );
      } else {
        alert(`Reactivation failed (HTTP ${status}). Check the browser console (F12) for details.`);
      }
      console.error('Reactivate error:', e.response || e);
    }
  };

  const revokeInvitation = async (id) => {
    if (!window.confirm('Revoke this invitation?')) return;
    await api.delete(`/admin/invitations/${id}`);
    load();
  };

  const resendInvitation = async (id) => {
    const r = await api.post(`/admin/invitations/${id}/resend`);
    const link = `${window.location.origin}${r.data.invite_link}`;
    setInviteResult({ link, expires: r.data.expires_at });
    load();
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    alert('Invite link copied to clipboard');
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Users &amp; invitations</h2>
        <button onClick={() => { setShowInvite(true); setInviteResult(null); }} style={btnPrimary}>+ Invite user</button>
      </div>

      {showInvite && (
        <form onSubmit={submitInvite} style={{ background: '#F8F9FB', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #E5E7EB' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 10 }}>
            <input type="email" placeholder="Email" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} required style={inp} />
            <input placeholder="Full name (optional)" value={inviteForm.full_name} onChange={e => setInviteForm({ ...inviteForm, full_name: e.target.value })} style={inp} />
            <select value={inviteForm.role} onChange={e => setInviteForm({ ...inviteForm, role: e.target.value })} style={inp}>
              <option value="verifier">Verifier</option>
              <option value="company_admin">Company Admin</option>
              {user.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
            </select>
            {user.role === 'super_admin' && inviteForm.role !== 'super_admin' && (
              <select value={inviteForm.company_id} onChange={e => setInviteForm({ ...inviteForm, company_id: e.target.value })} required style={inp}>
                <option value="">Select company…</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="submit" style={btnPrimary}>Send invitation</button>
            <button type="button" onClick={() => setShowInvite(false)} style={btnGhost}>Cancel</button>
          </div>
          {inviteResult && (
            <div style={{ marginTop: 14, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: 12, fontSize: 12 }}>
              <p style={{ margin: 0, fontWeight: 500, color: '#1D4ED8' }}>Invitation created — share this link:</p>
              <p style={{ margin: '6px 0', fontFamily: 'monospace', wordBreak: 'break-all', color: '#111827' }}>{inviteResult.link}</p>
              <button type="button" onClick={() => navigator.clipboard.writeText(inviteResult.link)} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px' }}>Copy link</button>
            </div>
          )}
        </form>
      )}

      {resetResult && (
        <div style={{ marginBottom: 14, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: 14, fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, marginRight: 12 }}>
              <p style={{ margin: 0, fontWeight: 600, color: '#1D4ED8' }}>
                Password reset link generated for {resetResult.userName || resetResult.userEmail}
              </p>
              <p style={{ margin: '6px 0', fontFamily: 'monospace', wordBreak: 'break-all', color: '#111827', fontSize: 11, padding: 8, background: '#fff', borderRadius: 4, border: '1px solid #DBEAFE' }}>
                {resetResult.link}
              </p>
              <p style={{ margin: '6px 0 0 0', fontSize: 11, color: '#1E40AF' }}>
                Send this link to <strong>{resetResult.userEmail}</strong> via Slack, Signal, or another secure channel.
                Valid until <strong>{new Date(resetResult.expiresAt).toLocaleString()}</strong>. One-time use only.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button onClick={() => { navigator.clipboard.writeText(resetResult.link); }}
                style={{ ...btnPrimary, fontSize: 11, padding: '6px 12px' }}>Copy link</button>
              <button onClick={() => setResetResult(null)}
                style={{ ...btnGhost, fontSize: 11, padding: '6px 12px' }}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        <button onClick={() => setTab('users')} style={tabBtn(tab === 'users')}>
          Active users <span style={countPill}>{users.filter(u => u.is_active).length}</span>
        </button>
        <button onClick={() => setTab('inactive')} style={tabBtn(tab === 'inactive')}>
          Inactive <span style={countPill}>{users.filter(u => !u.is_active).length}</span>
        </button>
        <button onClick={() => setTab('pending')} style={tabBtn(tab === 'pending')}>
          Pending invitations
          {invitations.length > 0 && <span style={{ ...countPill, background: '#FEF3C7', color: '#854D0E' }}>{invitations.length}</span>}
        </button>
      </div>

      {(tab === 'users' || tab === 'inactive') && (
        <div style={{ overflowX: 'auto' }}>
          <table style={tbl}>
            <thead><tr style={{ background: '#F8F9FB', textAlign: 'left' }}>
              <th style={th}>Email</th><th style={th}>Name</th><th style={th}>Role</th>
              <th style={th}>Company</th><th style={th}>Last login</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {users.filter(u => tab === 'users' ? u.is_active : !u.is_active).map(u => (
                <tr key={u.id} style={{ borderBottom: '0.5px solid #F3F4F6' }}>
                  <td style={td}>{u.email}</td>
                  <td style={td}>{u.full_name || '—'}</td>
                  <td style={td}><span style={roleBadge(u.role)}>{u.role.replace('_', ' ')}</span></td>
                  <td style={td}>{u.company_name || '—'}</td>
                  <td style={td}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</td>
                  <td style={td}>
                    {u.is_active && u.id !== user.id && (
                      <button onClick={() => resetPassword(u.id, u.email, u.full_name)} style={{ ...btnGhost, fontSize: 11, color: '#2563EB', marginRight: 4 }}>Reset password</button>
                    )}
                    {u.is_active
                      ? (u.id !== user.id && <button onClick={() => deactivate(u.id)} style={{ ...btnGhost, fontSize: 11, color: '#DC2626' }}>Deactivate</button>)
                      : <button onClick={() => reactivate(u.id, u.full_name || u.email)} style={{ ...btnGhost, fontSize: 11, color: '#16A34A' }}>Reactivate</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.filter(u => tab === 'users' ? u.is_active : !u.is_active).length === 0 && (
            <p style={{ color: '#9CA3AF', textAlign: 'center', padding: '20px 0' }}>No {tab} users.</p>
          )}
        </div>
      )}

      {tab === 'pending' && (
        <div style={{ overflowX: 'auto' }}>
          {invitations.length === 0 ? <p style={{ color: '#9CA3AF', textAlign: 'center', padding: '20px 0' }}>No pending invitations.</p> : (
            <table style={tbl}>
              <thead><tr style={{ background: '#F8F9FB', textAlign: 'left' }}>
                <th style={th}>Email</th><th style={th}>Role</th><th style={th}>Company</th>
                <th style={th}>Invited</th><th style={th}>Expires</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {invitations.map(i => (
                  <tr key={i.id} style={{ borderBottom: '0.5px solid #F3F4F6' }}>
                    <td style={td}>{i.email}</td>
                    <td style={td}><span style={roleBadge(i.role)}>{i.role.replace('_', ' ')}</span></td>
                    <td style={td}>{i.company_name || '—'}</td>
                    <td style={td}>{new Date(i.created_at).toLocaleDateString()}</td>
                    <td style={td}>{new Date(i.expires_at).toLocaleDateString()}</td>
                    <td style={td}>
                      <button onClick={() => copyLink(i.token)} style={{ ...btnGhost, fontSize: 11, padding: '4px 8px' }}>Copy link</button>
                      <button onClick={() => resendInvitation(i.id)} style={{ ...btnGhost, fontSize: 11, padding: '4px 8px', marginLeft: 4 }}>Reset</button>
                      <button onClick={() => revokeInvitation(i.id)} style={{ ...btnGhost, fontSize: 11, padding: '4px 8px', marginLeft: 4, color: '#DC2626' }}>Revoke</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const tabBtn = (active) => ({
  background: active ? '#fff' : 'transparent',
  border: active ? '1px solid #E5E7EB' : '1px solid transparent',
  borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
  fontWeight: active ? 500 : 400, fontSize: 13,
  color: active ? '#111827' : '#6B7280', display: 'flex', alignItems: 'center', gap: 8,
});
const countPill = { background: '#F3F4F6', color: '#6B7280', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 };
const roleBadge = (role) => {
  const c = role === 'super_admin' ? '#7C3AED' : role === 'company_admin' ? '#2563EB' : '#059669';
  return { fontSize: 10, padding: '2px 8px', borderRadius: 4, background: `${c}15`, color: c, fontWeight: 600, textTransform: 'uppercase' };
};
const card = { background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' };
const btnPrimary = { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnGhost = { background: 'transparent', color: '#374151', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 };
const inp = { padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { padding: '10px 12px', fontWeight: 500, fontSize: 11, color: '#6B7280', textTransform: 'uppercase', borderBottom: '1px solid #E5E7EB' };
const td = { padding: '10px 12px', color: '#111827' };
